<script setup lang="ts">
import { computed, ref } from 'vue';
import { sanitizeRegistration, validateRegistration } from '../api/checkVehicle';
import { scanImageFile } from '../composables/imageScanner';

const props = defineProps<{
  busy: boolean;
  error?: string | null;
}>();
const emit = defineEmits<{ lookup: [plate: string, region?: string] }>();

const plate = ref('');
const localError = ref<string | null>(null);
const scanning = ref(false);
const fileRef = ref<HTMLInputElement | null>(null);
const normalizedPlate = computed(() => sanitizeRegistration(plate.value));

function submit(): void {
  const validationError = validateRegistration(plate.value);
  if (validationError) {
    localError.value = validationError;
    return;
  }
  localError.value = null;
  emit('lookup', normalizedPlate.value);
}

function pickImage(): void {
  fileRef.value?.click();
}

async function onFile(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = ''; // allow re-picking the same file
  if (!file) return;
  scanning.value = true;
  localError.value = null;
  try {
    const result = await scanImageFile(file);
    if (!result) {
      localError.value = 'noPlate';
      return;
    }
    plate.value = result.text;
    emit('lookup', result.text, result.region);
  } catch {
    localError.value = 'noPlate';
  } finally {
    scanning.value = false;
  }
}
</script>

<template>
  <form class="manual-lookup" @submit.prevent="submit">
    <label class="manual-label" for="manual-plate">{{ $t('manual.label') }}</label>
    <div class="manual-row">
      <input
        id="manual-plate"
        v-model="plate"
        class="manual-input"
        inputmode="text"
        autocapitalize="characters"
        autocomplete="off"
        spellcheck="false"
        :placeholder="$t('manual.placeholder')"
        :disabled="props.busy || scanning"
      />
      <div class="manual-actions">
        <button
          type="button"
          class="btn manual-gallery"
          :aria-label="$t('aria.gallery')"
          :disabled="props.busy || scanning"
          @click="pickImage"
        >
          <span v-if="scanning" class="spinner small-spinner" aria-hidden="true"></span>
          <svg
            v-else
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="4.5" width="18" height="15" rx="2" />
            <circle cx="8.5" cy="9.5" r="1.6" />
            <path d="M4 17l4.5-4.5 3.5 3.5 3-3L20 16.5" />
          </svg>
        </button>
        <button class="btn primary manual-submit" type="submit" :disabled="props.busy || scanning">
          <span v-if="props.busy" class="spinner small-spinner" aria-hidden="true"></span>
          <span>{{ props.busy ? $t('manual.checking') : $t('manual.submit') }}</span>
        </button>
      </div>
    </div>
    <input
      ref="fileRef"
      class="visually-hidden"
      type="file"
      accept="image/*"
      @change="onFile"
    />
    <p v-if="scanning" class="manual-label">{{ $t('manual.scanningImage') }}</p>
    <p v-else-if="localError || props.error" class="lookup-error">
      {{ localError === 'noPlate' ? $t('manual.noPlateFound') : localError ? $t('manual.invalid') : props.error }}
    </p>
  </form>
</template>
